import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { copilotReturn } from "@/lib/embedded";
import { cn } from "@/lib/utils";
import { Stat, CompetencyScores, QualityChecks, ProctoringCard } from "@/components/assessment/TrialParts";
import { SignoffPanel } from "@/components/assessment/SignoffPanel";
import { ArrowLeft, FileDown } from "lucide-react";

const hexToRgb = (hex) => { const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || ""); return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [14, 124, 130]; };
const loadImg = (url) => new Promise((res) => {
  if (!url) return res(null);
  const img = new Image(); img.crossOrigin = "anonymous";
  img.onload = () => { try { const c = document.createElement("canvas"); c.width = img.naturalWidth; c.height = img.naturalHeight; c.getContext("2d").drawImage(img, 0, 0); res({ data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight }); } catch { res(null); } };
  img.onerror = () => res(null); img.src = url;
});

export default function TrialReport() {
  const { trialId } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [brand, setBrand] = useState(null);
  const [job, setJob] = useState(null);
  const [tick, setTick] = useState(0);
  const backTo = copilotReturn();

  const load = () => { setLoading(true); api.get(`/test/trial/${trialId}`).then((r) => setT(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message)).finally(() => setLoading(false)); };
  const refresh = () => { api.get(`/test/trial/${trialId}`).then((r) => setT(r.data)).catch(() => {}); setTick((n) => n + 1); };
  useEffect(load, [trialId]);
  useEffect(() => { api.get("/tenant/branding").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  const jobId = t?.job_id;
  useEffect(() => { if (jobId) api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => {}); }, [jobId, tick]);

  if (loading) return <LoadingState label="Loading the trial report…" />;
  if (err) return <div className="p-8 max-w-md space-y-4"><ErrorState message={err} /><Button variant="outline" onClick={() => nav("/recruiter/jobs")} data-testid="report-error-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Jobs</Button></div>;

  const items = (t.sections || []).flatMap((s) => s.items || []);
  const diff = { easy: 0, medium: 0, hard: 0 };
  items.forEach((it) => { if (diff[it.difficulty] !== undefined) diff[it.difficulty]++; });
  const comps = (t.rubric || []).map((r) => r.name).filter(Boolean);
  const weightSum = (t.rubric || []).reduce((a, r) => a + (Number(r.weight) || 0), 0);
  const balanced = weightSum === 100;
  const varietyOk = new Set(items.map((it) => it.difficulty).filter(Boolean)).size >= 2;
  const enough = items.length >= 3;
  const quality = Math.round(((enough ? 1 : 0) + (varietyOk ? 1 : 0) + (balanced ? 1 : 0) + (comps.length >= 2 ? 1 : 0)) / 4 * 100);
  const result = t.result;
  const signoffs = t.signoffs || [];

  const exportPdf = async () => {
    try {
      const primary = hexToRgb(brand?.brand_primary);
      const logo = await loadImg(brand?.brand_logo_url);
      const pdf = new jsPDF("p", "pt", "a4");
      const W = pdf.internal.pageSize.getWidth();
      const M = 48; let y = 60;
      const nl = (h) => { y += h; if (y > 780) { pdf.addPage(); y = 60; } };
      const write = (txt, { size = 11, bold = false, color = [40, 40, 40], gap = 6 } = {}) => {
        pdf.setFont("helvetica", bold ? "bold" : "normal"); pdf.setFontSize(size); pdf.setTextColor(...color);
        pdf.splitTextToSize(String(txt), W - M * 2).forEach((l) => { if (y > 790) { pdf.addPage(); y = 60; } pdf.text(l, M, y); y += size + 4; });
        y += gap;
      };
      const rule = () => { pdf.setDrawColor(220); pdf.line(M, y, W - M, y); nl(14); };
      if (logo) { const lw = 84; const lh = Math.min(logo.h * lw / logo.w, 40); pdf.addImage(logo.data, "PNG", W - M - lw, 40, lw, lh); }
      write(brand?.brand_name || "HireStream", { size: 10, bold: true, color: primary, gap: 2 });
      write("Assessment Quality Report", { size: 20, bold: true, color: primary });
      write(t.job_title || "Role", { size: 14, bold: true });
      write(`Source: ${t.source}   ·   ${t.time_limit_min ? t.time_limit_min + " min limit" : "Untimed"}   ·   Trial by ${t.created_by_name || "recruiter"}`, { size: 9, color: [120, 120, 120] });
      write(`Generated ${new Date().toLocaleString()}`, { size: 9, color: [120, 120, 120], gap: 10 });
      rule();
      write(`Quality score: ${quality}%  (${quality >= 75 ? "Launch-ready" : "Needs a look"})`, { size: 13, bold: true, color: primary });
      write(`${enough ? "PASS" : "CHECK"} — ${items.length} question(s) across ${(t.sections || []).length} section(s)`, { size: 10 });
      write(`${varietyOk ? "PASS" : "CHECK"} — Balanced difficulty mix (easy ${diff.easy}, medium ${diff.medium}, hard ${diff.hard})`, { size: 10 });
      write(`${balanced ? "PASS" : "CHECK"} — Rubric weights total ${weightSum}%`, { size: 10 });
      write(`${comps.length >= 2 ? "PASS" : "CHECK"} — ${comps.length} competencies covered`, { size: 10, gap: 10 });
      rule();
      write("Competencies & weights", { size: 12, bold: true, color: primary });
      (t.rubric || []).forEach((r) => write(`• ${r.name} — ${r.weight}%`, { size: 10, gap: 2 }));
      nl(8);
      if (result) {
        rule();
        write("Trial scoring preview", { size: 12, bold: true, color: primary });
        if (result.overall_score != null) write(`Overall: ${Math.round(result.overall_score)} (${result.confidence || "—"} confidence)`, { size: 10 });
        (result.competencies || []).forEach((c) => write(`• ${c.name}: ${c.score ?? "—"}`, { size: 10, gap: 2 }));
        if (result.rationale) { nl(4); write(result.rationale, { size: 9, color: [90, 90, 90] }); }
      }
      if (signoffs.length) {
        rule();
        write("Hiring-team sign-off", { size: 12, bold: true, color: primary });
        signoffs.forEach((s) => write(`• ${s.reviewer} — ${s.decision === "approved" ? "Approved" : "Needs changes"}${s.note ? ` — "${s.note}"` : ""} (${new Date(s.at).toLocaleDateString()})`, { size: 10, gap: 2 }));
      }
      nl(10);
      write(`Advisory only — AI recommends, humans decide. ${brand?.brand_name || "HireStream"}.`, { size: 8, color: [150, 150, 150] });
      pdf.save(`assessment-quality-${(t.job_title || "report").replace(/\s+/g, "-").toLowerCase()}.pdf`);
      toast.success("PDF exported");
    } catch { toast.error("Could not export PDF"); }
  };

  const cleared = signoffs.some((s) => s.decision === "approved");
  const status = cleared ? ["Approved", "bg-primary/12 text-primary"] : signoffs.some((s) => s.decision === "needs_changes") ? ["Changes requested", "bg-rose-500/12 text-rose-600 dark:text-rose-300"] : [result ? "Trial complete · ready to approve" : "Trial not finished", "bg-amber-400/12 text-amber-700 dark:text-amber-300"];
  const checks = [
    { ok: enough, label: `${items.length} question${items.length === 1 ? "" : "s"} across ${(t.sections || []).length} section(s)` },
    { ok: varietyOk, label: "Balanced difficulty mix" },
    { ok: balanced, label: `Rubric weights total ${weightSum}%` },
    { ok: comps.length >= 2, label: `${comps.length} competenc${comps.length === 1 ? "y" : "ies"} covered` },
  ];

  return (
    <div className="app-container py-5 flex flex-col gap-4 md:h-[calc(100dvh-60px)]" data-testid="trial-report">
      <div className="shrink-0 space-y-4" data-testid="report-pinned">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1.5 min-w-0">
            <button onClick={() => nav(backTo || `/recruiter/jobs/${t.job_id}/test`)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="report-back"><ArrowLeft className="h-4 w-4" /> {backTo ? "Back to conversation" : "Back to assessment"}</button>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Trial report</span>
              <h1 className="text-xl sm:text-2xl font-display font-semibold tracking-tight [overflow-wrap:anywhere]">{t.job_title}</h1>
              <span className={cn("px-2.5 py-0.5 rounded-full text-xs font-medium", status[1])} data-testid="report-status">{status[0]}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t.source} · {t.time_limit_min ? `${t.time_limit_min} min limit` : "Untimed"} · trial by {t.created_by_name || "recruiter"}</p>
          </div>
          <Button variant="outline" size="sm" onClick={exportPdf} className="gap-1.5" data-testid="report-export-pdf"><FileDown className="h-4 w-4" /> Download PDF</Button>
        </div>
        <SignoffPanel t={t} job={job} onChanged={refresh} />
      </div>
      <div className="space-y-6 md:flex-1 md:min-h-0 md:overflow-y-auto md:overscroll-contain md:-mx-1 md:px-1 md:pb-4" data-testid="report-body">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="report-stats">
          <Stat value={result?.overall_score != null ? Math.round(result.overall_score) : "—"} label="Your trial score" testId="report-stat-score" />
          <Stat value={items.length} label={`questions · ${(t.sections || []).length} sections`} testId="report-stat-questions" />
          <Stat value={result?.integrity_score ?? "—"} label={`Integrity · ${(result?.integrity_label || "not scored").toLowerCase()}`} tone="primary" testId="report-stat-integrity" />
          <Stat value={`${quality}%`} label={quality >= 75 ? "Quality · launch-ready" : "Quality · needs a look"} tone={quality >= 75 ? undefined : "amber"} testId="report-quality" />
        </div>
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
          <div className="space-y-4 min-w-0">
            <CompetencyScores result={result} rubric={t.rubric} />
            <QualityChecks checks={checks} diff={diff} />
          </div>
          <aside className="space-y-4 min-w-0">
            <ProctoringCard result={result} />
          </aside>
        </div>
      </div>
    </div>
  );
}

